// src/pages/ChatWithAI/hooks/useAttachments.js
import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';

// codCli/codProcesso: cliente e processo a que o consumo dos anexos é atribuído (chat_log)
export function useAttachments(threadId, onThreadCreated, { codCli = null, codProcesso = null } = {}) {
  const [anexos, setAnexos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [localThreadId, setLocalThreadId] = useState(threadId);

  // Atualizar threadId local quando prop mudar
  useEffect(() => {
    if (threadId) {
      setLocalThreadId(threadId);
    }
  }, [threadId]);

  /**
   * Criar thread automaticamente se não existir
   */
  const ensureThread = useCallback(async () => {
    if (localThreadId) {
      return localThreadId;
    }

    try {
      console.log('📝 Criando thread automaticamente para anexo...');
      
      const response = await axios.post('/api/chat-threads/create', {
        titulo: 'Nova conversa',
        id_law_case: null
      });

      if (response.data.success) {
        const newThreadId = response.data.data.id;
        setLocalThreadId(newThreadId);
        
        // Notificar componente pai sobre nova thread
        if (onThreadCreated) {
          onThreadCreated(newThreadId);
        }

        console.log('✅ Thread criada automaticamente:', newThreadId);
        return newThreadId;
      }
    } catch (err) {
      console.error('❌ Erro ao criar thread:', err);
      throw new Error('Não foi possível criar thread para anexar');
    }
  }, [localThreadId, onThreadCreated]);

  /**
   * Buscar anexos ativos da thread
   * @param {number|null} explicitThreadId - ThreadId explícito para evitar stale closure
   */
  const fetchAnexos = useCallback(async (explicitThreadId = null) => {
    const activeThreadId = explicitThreadId || localThreadId || threadId;
    
    if (!activeThreadId) {
      console.log('⚠️ fetchAnexos: sem threadId ativo');
      setAnexos([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      console.log('🔍 Buscando anexos para thread:', activeThreadId);
      const response = await axios.get(`/api/attachments/${activeThreadId}`);

      if (response.data.success) {
        console.log('✅ Anexos carregados:', response.data.data.length);
        setAnexos(response.data.data);
      }
    } catch (err) {
      console.error('❌ Erro ao buscar anexos:', err);
      setError(err.response?.data?.message || 'Erro ao carregar anexos');
    } finally {
      setLoading(false);
    }
  }, [localThreadId, threadId]);

  /**
   * Anexar ficheiro (upload)
   */
  const anexarFicheiro = useCallback(async (file) => {
    try {
      setLoading(true);
      setError(null);

      // Garantir que existe thread
      const activeThreadId = await ensureThread();

      const formData = new FormData();
      formData.append('file', file);
      formData.append('threadId', activeThreadId);
      if (codCli) formData.append('codCli', codCli);
      if (codProcesso) formData.append('codProcesso', codProcesso);

      console.log('📤 Anexando ficheiro na thread:', activeThreadId);
      const response = await axios.post('/api/attachments/ficheiro', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        console.log('✅ Ficheiro anexado, recarregando lista...');
        // 🔥 Passar threadId explicitamente para evitar stale closure
        await fetchAnexos(activeThreadId);
        return response.data.data;
      }
    } catch (err) {
      console.error('❌ Erro ao anexar ficheiro:', err);
      throw new Error(err.response?.data?.message || 'Erro ao anexar ficheiro');
    } finally {
      setLoading(false);
    }
  }, [ensureThread, fetchAnexos, codCli, codProcesso]);

  /**
   * Anexar documento existente
   */
  const anexarDocumento = useCallback(async (documentoId) => {
    try {
      setLoading(true);
      setError(null);

      const activeThreadId = await ensureThread();

      console.log('📤 Anexando documento na thread:', activeThreadId);
      const response = await axios.post('/api/attachments/documento', {
        threadId: activeThreadId,
        documentoId,
        codCli,
        codProcesso
      });

      if (response.data.success) {
        console.log('✅ Documento anexado, recarregando lista...');
        // 🔥 Passar threadId explicitamente
        await fetchAnexos(activeThreadId);
        return response.data.data;
      }
    } catch (err) {
      console.error('❌ Erro ao anexar documento:', err);
      throw new Error(err.response?.data?.message || 'Erro ao anexar documento');
    } finally {
      setLoading(false);
    }
  }, [ensureThread, fetchAnexos, codCli, codProcesso]);

  /**
   * Anexar caso existente
   */
  const anexarCaso = useCallback(async (casoId) => {
    try {
      setLoading(true);
      setError(null);

      const activeThreadId = await ensureThread();

      console.log('📤 Anexando caso na thread:', activeThreadId);
      const response = await axios.post('/api/attachments/caso', {
        threadId: activeThreadId,
        casoId,
        codCli,
        codProcesso
      });

      if (response.data.success) {
        console.log('✅ Caso anexado, recarregando lista...');
        // 🔥 Passar threadId explicitamente
        await fetchAnexos(activeThreadId);
        return response.data.data;
      }
    } catch (err) {
      console.error('❌ Erro ao anexar caso:', err);
      throw new Error(err.response?.data?.message || 'Erro ao anexar caso');
    } finally {
      setLoading(false);
    }
  }, [ensureThread, fetchAnexos, codCli, codProcesso]);

  /**
   * Anexar cliente (busca API externa)
   */
  const anexarCliente = useCallback(async (clientCode) => {
    try {
      setLoading(true);
      setError(null);

      const activeThreadId = await ensureThread();

      console.log('📤 Anexando cliente na thread:', activeThreadId);
      const response = await axios.post('/api/attachments/cliente', {
        threadId: activeThreadId,
        clientCode,
        codCli,
        codProcesso
      });

      if (response.data.success) {
        console.log('✅ Cliente anexado, recarregando lista...');
        // 🔥 Passar threadId explicitamente
        await fetchAnexos(activeThreadId);
        return response.data.data;
      }
    } catch (err) {
      console.error('❌ Erro ao anexar cliente:', err);
      throw new Error(err.response?.data?.message || 'Erro ao buscar cliente');
    } finally {
      setLoading(false);
    }
  }, [ensureThread, fetchAnexos, codCli, codProcesso]);

  /**
   * Anexar texto pré-transcrito (OCR / Speech-to-Text via AnexarUnificadoDialog)
   */
  const anexarTextoTranscrito = useCallback(async ({ nome, conteudo }) => {
    try {
      setLoading(true);
      setError(null);

      const activeThreadId = await ensureThread();

      console.log('📤 Anexando texto transcrito na thread:', activeThreadId);
      const response = await axios.post('/api/attachments/texto', {
        threadId: activeThreadId,
        nome,
        conteudo,
        codCli,
        codProcesso,
      });

      if (response.data.success) {
        console.log('✅ Texto transcrito anexado, recarregando lista...');
        await fetchAnexos(activeThreadId);
        return response.data.data;
      }
    } catch (err) {
      console.error('❌ Erro ao anexar texto transcrito:', err);
      throw new Error(err.response?.data?.message || 'Erro ao anexar ficheiro transcrito');
    } finally {
      setLoading(false);
    }
  }, [ensureThread, fetchAnexos, codCli, codProcesso]);

  /**
   * Remover anexo (soft delete)
   */
  const removerAnexo = useCallback(async (anexoId) => {
    try {
      setLoading(true);
      setError(null);

      console.log('🗑️ Removendo anexo:', anexoId);
      const response = await axios.patch(`/api/attachments/${anexoId}/remover`);

      if (response.data.success) {
        console.log('✅ Anexo removido, recarregando lista...');
        // Usar threadId atual (não precisa de explícito aqui)
        await fetchAnexos();
        return true;
      }
    } catch (err) {
      console.error('❌ Erro ao remover anexo:', err);
      throw new Error(err.response?.data?.message || 'Erro ao remover anexo');
    } finally {
      setLoading(false);
    }
  }, [fetchAnexos]);

  /**
   * Montar contexto completo (anexos ativos concatenados)
   */
  const getContextoAnexos = useCallback(() => {
    if (!anexos || anexos.length === 0) return '';

    const textos = anexos.map(anexo => {
      const tipo = {
        file: '📄 Ficheiro',
        document: '📝 Documento',
        case: '📁 Caso',
        client: '👤 Cliente'
      }[anexo.tipo] || '📎 Anexo';

      return `\n---\n${tipo} (anexado)\n${anexo.content_text}\n`;
    });

    return textos.join('\n');
  }, [anexos]);

  // Carregar anexos quando threadId mudar
  useEffect(() => {
    if (localThreadId || threadId) {
      fetchAnexos();
    }
  }, [localThreadId, threadId, fetchAnexos]);

  return {
    anexos,
    loading,
    error,
    anexarFicheiro,
    anexarTextoTranscrito,
    anexarDocumento,
    anexarCaso,
    anexarCliente,
    removerAnexo,
    getContextoAnexos,
    refresh: fetchAnexos,
    currentThreadId: localThreadId || threadId
  };
}